import React from "react";
import { Alert, Card, Col, DatePicker, Empty, Row, Select, Spin, Table, Typography } from "antd";
import { Line } from "@ant-design/plots";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import {
  ItemQuantityConsumptionTrendResponse,
  ItemQuantitySnapshotListResponse,
  ItemQuantitySnapshotListItem,
} from "../../types/api";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type InventoryViewProps = {
  restaurantId: string;
};

export const InventoryView = (props: InventoryViewProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notifyError } = useNotification();

  // Default range: last 30 days.
  const defaultStart = dayjs().startOf("day").subtract(29, "days");
  const defaultEnd = dayjs().startOf("day").add(1, "days");

  const [snapshots, setSnapshots] = React.useState<ItemQuantitySnapshotListItem[] | null>(null);
  const [snapshotsLoading, setSnapshotsLoading] = React.useState<boolean>(true);

  const [selectedItemSchemaId, setSelectedItemSchemaId] = React.useState<string | undefined>(
    undefined
  );
  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs]>([defaultStart, defaultEnd]);
  const [trend, setTrend] = React.useState<ItemQuantityConsumptionTrendResponse | null>(null);
  const [trendLoading, setTrendLoading] = React.useState<boolean>(false);

  // ---- Fetch the snapshot list (current inventory for all limited items) ----
  // NOTE: this is a plain function (not useCallback) so it captures the latest
  // `t` / `notifyError` from the closure. The effect below only depends on
  // `props.restaurantId`, so it does NOT re-run every render — matching the
  // pattern in StatsView. Putting `notifyError` (which is a fresh function
  // reference on every render from useNotification) into a useCallback's deps
  // is what caused the previous infinite loop.
  const fetchSnapshots = () => {
    setSnapshotsLoading(true);
    getFetchT<ItemQuantitySnapshotListResponse>(
      `${API_URL}/v1/r/${props.restaurantId}/item_quantity_consumption/snapshots`,
      (resp) => {
        setSnapshots(resp.items ?? []);
        setSnapshotsLoading(false);
      },
      (err) => {
        setSnapshotsLoading(false);
        notifyError(t("error"), err);
      }
    );
  };

  React.useEffect(fetchSnapshots, [props.restaurantId]);

  // ---- Fetch the trend for the selected item + date range ----
  const fetchTrend = () => {
    if (!selectedItemSchemaId) {
      setTrend(null);
      return;
    }
    const [start, end] = dateRange;
    if (!start || !end) {
      return;
    }
    setTrendLoading(true);
    const startStr = start.format();
    const endStr = end.format();
    getFetchT<ItemQuantityConsumptionTrendResponse>(
      `${API_URL}/v1/r/${props.restaurantId}/item_quantity_consumption/trend` +
        `?item_schema_id=${encodeURIComponent(selectedItemSchemaId)}` +
        `&start=${encodeURIComponent(startStr)}` +
        `&end=${encodeURIComponent(endStr)}`,
      (resp) => {
        setTrend(resp);
        setTrendLoading(false);
      },
      (err) => {
        setTrendLoading(false);
        notifyError(t("error"), err);
      }
    );
  };

  React.useEffect(fetchTrend, [props.restaurantId, selectedItemSchemaId, dateRange]);

  // ---- Auto-select the first limited item when the snapshot list loads ----
  React.useEffect(() => {
    if (snapshots && snapshots.length > 0 && selectedItemSchemaId === undefined) {
      setSelectedItemSchemaId(snapshots[0].item_schema_id);
    }
  }, [snapshots, selectedItemSchemaId]);

  // ---- Build trend chart data ----
  const chartData = React.useMemo(() => {
    if (!trend?.points) return [];
    return trend.points.flatMap((p) => {
      const date = dayjs(p.date).format("YYYY-MM-DD");
      const points: { date: string; value: number; category: string }[] = [
        { date, value: p.consumed, category: t("consumed") },
        { date, value: p.reserved, category: t("reserved") },
      ];
      if (p.remaining != null) {
        points.push({ date, value: p.remaining, category: t("remaining") });
      }
      return points;
    });
  }, [trend, t]);

  const cardStyle = {
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorder}`,
    boxShadow: token.boxShadowTertiary,
  };

  // ---- Snapshot table columns ----
  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("quantity_limit"),
      dataIndex: "quantity_limit",
      key: "quantity_limit",
      width: 120,
      render: (v?: number) => (v != null ? v : t("na")),
    },
    {
      title: t("consumed"),
      dataIndex: "consumed",
      key: "consumed",
      width: 120,
    },
    {
      title: t("reserved"),
      dataIndex: "reserved",
      key: "reserved",
      width: 120,
    },
    {
      title: t("remaining"),
      dataIndex: "remaining",
      key: "remaining",
      width: 120,
      render: (v?: number) => (v != null ? v : t("na")),
    },
  ];

  return (
    <div>
      {/* Snapshot list */}
      <Card
        title={t("limited_quantity_inventory")}
        style={{ ...cardStyle, marginBottom: token.marginXL }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {snapshotsLoading ? (
          <div style={{ textAlign: "center", padding: token.paddingXL }}>
            <Spin size="large" />
          </div>
        ) : !snapshots || snapshots.length === 0 ? (
          <Empty description={t("no_limited_quantity_items")} />
        ) : (
          <Table
            rowKey="item_schema_id"
            columns={columns}
            dataSource={snapshots}
            pagination={false}
            size="middle"
            onRow={(row) => ({
              onClick: () => setSelectedItemSchemaId(row.item_schema_id),
              style: {
                cursor: "pointer",
                background:
                  row.item_schema_id === selectedItemSchemaId ? token.colorPrimaryBg : undefined,
              },
            })}
          />
        )}
      </Card>

      {/* Trend chart */}
      <Card
        title={t("consumption_trend")}
        style={cardStyle}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Row gutter={[16, 16]} align="middle" style={{ marginBottom: token.marginMD }}>
          <Col xs={24} md={12}>
            <Select
              style={{ width: "100%" }}
              placeholder={t("select_item")}
              value={selectedItemSchemaId}
              onChange={(v: string) => setSelectedItemSchemaId(v)}
              options={(snapshots ?? []).map((s) => ({
                value: s.item_schema_id,
                label: s.name,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={12}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="YYYY/MM/DD"
              disabledDate={(current) => {
                if (!current) return false;
                if (dateRange && dateRange[0]) {
                  const maxDate = dateRange[0].add(366, "days");
                  if (current.isAfter(maxDate, "day")) {
                    return true;
                  }
                }
                return false;
              }}
              style={{ width: "100%" }}
              placeholder={[t("start_date"), t("end_date")]}
            />
          </Col>
        </Row>

        {!selectedItemSchemaId ? (
          <Empty description={t("select_an_item_to_view_trend")} />
        ) : trendLoading ? (
          <div style={{ textAlign: "center", padding: token.paddingXL }}>
            <Spin size="large" />
          </div>
        ) : !trend || !trend.points || trend.points.length === 0 ? (
          <Empty description={t("no_trend_data")} />
        ) : (
          <Line
            data={chartData}
            xField="date"
            yField="value"
            colorField="category"
            shapeField="smooth"
            height={320}
            style={{ lineWidth: 2 }}
            scale={{ y: { nice: true } }}
            axis={{
              y: {
                label: {
                  formatter: (v: string) => Math.round(Number(v)).toString(),
                },
              },
            }}
            legend={{ color: { position: "top" as const } }}
            tooltip={{
              shared: true,
              items: [
                {
                  channel: "y",
                  valueFormatter: (value: number) => value.toString(),
                },
              ],
            }}
          />
        )}
      </Card>
    </div>
  );
};
